import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, delay, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as configurationActions from './configuration.actions';
import { forkJoin, Observable, of, pipe } from 'rxjs';
import { ConfigurationDataService } from '../configuration-data.service';
import { IConfiguration } from '@wap/configuration';
import { select, Store } from '@ngrx/store';
import { User } from '@wam/shared';
import { authenticationSelectors } from '@wam/authentication';
import { isNil } from 'lodash-es';
import { UI_LOCK_LONG_POLLING_INTERVAL_MS } from '@app/constants';
import { PrivateConfigurations } from '@app/shared/models/private-configurations.enum';

@Injectable()
export class ConfigurationEffects {
  private actions$ = inject(Actions);
  private configurationService = inject(ConfigurationDataService);
  private store = inject(Store);

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(configurationActions.load),
      withLatestFrom(this.store.select(authenticationSelectors.getCurrentUser)),
      switchMap(([_, user]) =>
        this.configurationService.load().pipe(this.onConfigurationsLoaded(user)),
      ),
    ),
  );

  longPollUiLock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(configurationActions.pollUiLock),
      delay(UI_LOCK_LONG_POLLING_INTERVAL_MS),
      withLatestFrom(this.store.pipe(select(authenticationSelectors.getCurrentUser))),
      switchMap(([_, user]) =>
        this.configurationService
          .load()
          .pipe(
            switchMap((configuration: IConfiguration) =>
              configuration[PrivateConfigurations.ORGANIZATION_UPDATES_LOCKED]
                ? of(configurationActions.pollUiLock())
                : of(configuration).pipe(this.onConfigurationsLoaded(user)),
            ),
          ),
      ),
    ),
  );

  private readonly onConfigurationsLoaded = (user: User) =>
    pipe(
      switchMap((configuration: IConfiguration) => this.migrate(configuration, user)),
      switchMap((configuration: IConfiguration) => {
        const courseConfiguration = this.safeParseJson(configuration.COURSE_CONFIG as string, {});

        if (isNil(configuration.language)) {
          configuration.language = 'en';
        }

        if (isNil(configuration.tabPosition)) {
          configuration.tabPosition = 'top';
        }

        return [
          configurationActions.loadSuccess({ configuration }),
          configurationActions.loadCourseConfigurationsSuccess({ courseConfiguration }),
          ...(configuration[PrivateConfigurations.ORGANIZATION_UPDATES_LOCKED]
            ? [configurationActions.pollUiLock()]
            : []),
        ];
      }),
      catchError((error) => [
        configurationActions.loadFail({ error: error?.toString() ?? 'Unknown error' }),
      ]),
    );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(configurationActions.createOrUpdate),
      mergeMap((configuration) =>
        this.configurationService
          .save(
            configuration.key,
            configuration.value,
            configuration.organization,
            configuration.school,
            configuration.scope,
          )
          .pipe(
            map((response: { data: { attributes: IConfiguration } }) =>
              configurationActions.createSuccess({ configuration: response.data.attributes }),
            ),
            catchError((error) => of(configurationActions.createFail(error?.toString()))),
          ),
      ),
    ),
  );

  saveUserPrivateConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(configurationActions.saveUserConfiguration),
      mergeMap(({ key, value }) =>
        this.configurationService.savePrivate(key, value).pipe(
          map((response: { data: { attributes: IConfiguration } }) =>
            configurationActions.saveUserConfigurationSuccess({
              configuration: response.data.attributes,
            }),
          ),
          catchError((error) =>
            of(configurationActions.saveUserConfigurationFail(error?.toString())),
          ),
        ),
      ),
    ),
  );

  private safeParseJson<T>(json: string, defaultValue: Partial<T>): T {
    try {
      return JSON.parse(json) || (defaultValue as T);
    } catch (e) {
      return defaultValue as T;
    }
  }

  private mapWelcomeDialog(user: User): boolean {
    const emails = this.safeParseJson(localStorage.getItem('welcomeDialog'), []) as string[];
    return emails.includes(user.email);
  }

  private migrate(configuration: IConfiguration, user: User): Observable<IConfiguration> {
    const userConfig = {
      tabPosition: localStorage.getItem('tabPosition'),
      language: localStorage.getItem('language'),
      eula: !!localStorage.getItem('eula'),
      welcomeDialog: this.mapWelcomeDialog(user),
    };

    const query = Object.entries(userConfig)
      .filter(([, value]) => value)
      .map(([key, value]) =>
        this.configurationService.savePrivate(key, value).pipe(
          tap(() => localStorage.removeItem(key)),
          catchError(() => of(null)),
        ),
      );
    query.push(of(undefined)); // forkJoin works incorect if query will be empty
    return forkJoin(query).pipe(map(() => ({ ...userConfig, ...configuration })));
  }
}
